import { create, v } from '@dojo/framework/core/vdom';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { focus } from '@dojo/framework/core/middleware/focus';
import { theme } from '@dojo/framework/core/middleware/theme';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ThemeProperties } from '@dojo/framework/core/middleware/theme';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { DNode, WNodeFactory, WidgetProperties } from '@dojo/framework/core/interfaces';

import { Keys } from '@holmescorp/common';

import css from '../css/inline-trigger-popup.m.css';

export interface InlineTriggerPopupProperties {
	key: string;
}

export interface InlineTriggerPopupChildren {
	trigger(toggleOpen: () => void): any;
	content(onClose: () => void): any;
}

interface InlineTriggerPopupIcache {
	open: boolean;
	wrapperRef: HTMLElement | null;
}

const FOCUSABLE_SELECTOR =
	'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function trapFocus(event: KeyboardEvent) {
	if (event.key !== 'Tab') return;
	const container = event.currentTarget as HTMLElement;
	const focusable = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
	if (focusable.length === 0) return;
	const first = focusable[0];
	const last = focusable[focusable.length - 1];
	if (event.shiftKey) {
		if (document.activeElement === first) {
			event.preventDefault();
			last.focus();
		}
	} else {
		if (document.activeElement === last) {
			event.preventDefault();
			first.focus();
		}
	}
}

const icache = createICacheMiddleware<InlineTriggerPopupIcache>();
const factory = create({ icache, focus, theme })
	.properties<InlineTriggerPopupProperties>()
	.children<InlineTriggerPopupChildren[]>();

export const InlineTriggerPopup = factory(function ({ properties, children, middleware: { icache, focus, theme } }) {
	const { key } = properties();
	const themeCss = theme.classes(css);
	const isOpen = icache.getOrSet('open', false);

	function toggleOpen() {
		const nowOpen = !isOpen;
		icache.set('open', nowOpen);

		if (nowOpen) {
			// Defer to next tick to ensure DOM has rendered
			setTimeout(() => {
				const node = document.getElementById(key); // Or use a unique class if no ID
				if (node) {
					icache.set('wrapperRef', node);
					node.focus();
					document.addEventListener('click', handleOutsideClick, true);
				}
			}, 0);
		} else {
			document.removeEventListener('click', handleOutsideClick, true);
		}
	}

	function close() {
		icache.set('open', false);
		focus.focus();
		document.removeEventListener('click', handleOutsideClick, true);
	}

	function handleOutsideClick(event: MouseEvent) {
		const node = icache.get('wrapperRef');
		if (node && !node.contains(event.target as Node)) {
			close();
		}
	}

	const [{ trigger, content }] = children();

	return v(
		'div',
		{
			key,
			id: key,
			tabIndex: -1,
			classes: [themeCss.inlinePopupWrapper],
			onkeydown: (event) => {
				switch (event.key) {
					case Keys.Escape:
						if (isOpen) {
							event.stopPropagation();
							close();
						}
				}
			}
		},
		[
			trigger(toggleOpen),
			isOpen ? v('div', { classes: [themeCss.inlinePopupContent], onkeydown: trapFocus }, [content(close)]) : null
		]
	);
});

export default InlineTriggerPopup;
